"use client"

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

function cx(...args: Array<string | false | undefined | null>) {
  return args.filter(Boolean).join(' ')
}

type SidebarContextValue = {
  collapsed: boolean
  toggle: () => void
}
const SidebarCtx = createContext<SidebarContextValue | null>(null)

export function useSidebar() {
  const ctx = useContext(SidebarCtx)
  if (!ctx) throw new Error('useSidebar must be used within <Sidebar>')
  return ctx
}

type SidebarProps = React.HTMLAttributes<HTMLElement> & {
  collapsible?: 'icon' | 'none'
}
export function Sidebar({ collapsible = 'icon', className, children, ...rest }: SidebarProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      const raw = localStorage.getItem('sidebar.collapsed')
      return raw === '1'
    } catch {
      return false
    }
  })
  const ctx = useMemo(() => ({ collapsed, toggle: () => setCollapsed((s) => !s) }), [collapsed])
  const width = collapsed && collapsible === 'icon' ? 'w-[60px]' : 'w-[228px]'
  // Allow external components to toggle via a custom event
  useEffect(() => {
    const handler = () => ctx.toggle()
    window.addEventListener('sidebar:toggle', handler)
    return () => window.removeEventListener('sidebar:toggle', handler)
  }, [ctx])
  // Persist collapsed state
  useEffect(() => {
    try { localStorage.setItem('sidebar.collapsed', collapsed ? '1' : '0') } catch {}
  }, [collapsed])
  return (
    <SidebarCtx.Provider value={ctx}>
      <aside
        data-collapsed={collapsed ? '' : undefined}
        className={cx('group/sidebar h-dvh min-h-dvh sticky top-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground flex flex-col transition-[width] duration-200', width, className || '')}
        {...rest}
      >
        {children}
      </aside>
    </SidebarCtx.Provider>
  )
}

export function SidebarMenuBadge({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cx(
        'ml-auto inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-medium text-foreground/80',
        className || '',
      )}
      {...props}
    />
  )
}

export function SidebarHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('px-3 py-2.5 border-b border-sidebar-border shrink-0', className || '')} {...props} />
}
export function SidebarContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('flex-1 overflow-auto min-h-0', className || '')} {...props} />
}
export function SidebarFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('px-3 py-2 border-t border-sidebar-border shrink-0', className || '')} {...props} />
}

export function SidebarGroup({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('py-1.5', className || '')} {...props} />
}
export function SidebarGroupLabel({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { collapsed } = useSidebar()
  return (
    <div className={cx('px-3 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground', collapsed && 'sr-only', className || '')} {...props} />
  )
}
export function SidebarGroupContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('', className || '')} {...props} />
}

export function SidebarMenu({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) {
  return <ul className={cx('px-1', className || '')} {...props} />
}
export function SidebarMenuItem({ className, ...props }: React.LiHTMLAttributes<HTMLLIElement>) {
  return <li className={cx('', className || '')} {...props} />
}

type SidebarMenuButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean
  tooltip?: string
  isActive?: boolean
  children: React.ReactElement | React.ReactElement[]
}
export function SidebarMenuButton({ asChild, tooltip, isActive, children, className, ...rest }: SidebarMenuButtonProps) {
  const { collapsed } = useSidebar()

  if (asChild && React.isValidElement(children)) {
    // Clone the child (e.g., Next Link) and inject classes + collapse behavior
    const child = children as React.ReactElement<any>
    const origChildren = child.props.children as any[]
    const processedChildren = React.Children.map(origChildren, (ch: any) => {
      if (collapsed && React.isValidElement(ch) && ch.type === 'span') {
        return React.cloneElement(ch as any, { className: cx('opacity-0 w-0 select-none pointer-events-none', (ch as any)?.props?.className || '') })
      }
      return ch
    })
    return React.cloneElement(child, {
      title: tooltip ?? child.props.title,
      className: cx(
        'flex w-full items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-muted',
        isActive ? 'bg-muted text-foreground' : 'text-muted-foreground',
        collapsed && 'justify-center',
        child.props.className || '',
        className || '',
      ),
      children: processedChildren,
      ...rest,
    })
  }

  return (
    <button
      title={tooltip}
      className={cx(
        'flex w-full items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-muted',
        isActive ? 'bg-muted text-foreground' : 'text-muted-foreground',
        collapsed && 'justify-center',
        className || '',
      )}
      {...rest}
    >
      {children}
    </button>
  )
}

export function SidebarRail({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { toggle, collapsed } = useSidebar()
  return (
    <button
      aria-label="Toggle sidebar"
      onClick={toggle}
      className={cx(
        'absolute -right-3 top-12 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full border border-sidebar-border bg-sidebar shadow',
        className || '',
      )}
      {...props}
    >
      <svg className={cx('w-3 h-3 transition-transform', !collapsed && 'rotate-180')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 18l-6-6 6-6" />
      </svg>
    </button>
  )
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

export function SidebarTrigger({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      aria-label="Toggle sidebar"
      onClick={() => {
        try {
          window.dispatchEvent(new Event('sidebar:toggle'))
        } catch {}
      }}
      className={cx('inline-flex items-center justify-center', className || '')}
      {...props}
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12h18M3 6h18M3 18h18" />
      </svg>
    </button>
  )
}
